// @ts-check
import React from 'react';
import PropTypes from 'prop-types';
import { Typography, Container } from '@material-ui/core';
import cx from 'classnames';
import { formatOrderId } from 'utils';
import _isEmpty from 'lodash/isEmpty';
import ShippingAddress from 'modules/clients/components/ShippingAddress';
import BopsAddress from 'modules/clients/components/BopsAddress';
import { TRANSACTION_TYPES } from 'constants/index';

const GeneralOrderInfo = ({
  classes,
  email,
  orderId,
  transactionType,
  shipmentMethodName,
  shippingAddress,
  storeAddressInfo,
  firstName,
  lastName,
  bopsAddress,
  isBopsOrder,
  isVoid,
  isReturn,
}) => {
  // Void and return order info have several same conditions
  const isRefundState = isVoid || isReturn;
  const isStoreAddressNeed = !_isEmpty(storeAddressInfo);

  const renderTitleMessage = () => {
    if (isRefundState) {
      return (
        <Typography data-testid="title-message-paragraph" paragraph classes={{ root: classes.confirmationDescription }}>
          {isVoid
            ? `The order for ${firstName} ${lastName} has been voided.`
            : `One or more items for ${firstName} ${lastName} has been processed for return.`}
        </Typography>
      );
    }

    return (
      <Typography data-testid="order-placed-text" paragraph classes={{ root: classes.description }}>
        Your order has been placed for {firstName} {lastName}. An e-receipt has been sent to
        <span className={classes.email}> {email} </span>
        and a copy has been sent to you. Check the order status for updates.
      </Typography>
    );
  };

  const renderAddressItem = (address, label) => {
    if (_isEmpty(address)) {
      return;
    }

    return (
      <Container classes={{ root: classes.orderDeliveryInfo }}>
        <Typography variant="h4" className={classes.mainInfoHeader}>
          {label}
        </Typography>
        {isBopsOrder ? <BopsAddress address={address} /> : <ShippingAddress address={address} />}
      </Container>
    );
  };

  const renderDeliveryMethod = () => {
    if (!shipmentMethodName) {
      return;
    }

    return (
      <Container classes={{ root: classes.orderDeliveryInfo }}>
        <Typography variant="h4" className={classes.mainInfoHeader}>
          Delivery Method
        </Typography>
        <Typography paragraph>{shipmentMethodName}</Typography>
      </Container>
    );
  };

  return (
    // Data is hardcoded for now, will need to pass all the proper data when we have it
    <Container maxWidth="sm" classes={{ root: cx(classes.infoBlock, classes.borderBottom) }}>
      {renderTitleMessage()}
      <Container classes={{ root: classes.orderDeliveryInfo }}>
        <Typography variant="h4" className={classes.mainInfoHeader}>
          Order No.
        </Typography>
        <Typography variant="h2">{formatOrderId(orderId)}</Typography>
      </Container>
      {transactionType === TRANSACTION_TYPES.SENDS ? (
        <>
          {isRefundState && (
            <Typography variant="h4" className={classes.mainInfoHeader}>
              SEND
            </Typography>
          )}
          {renderAddressItem(shippingAddress, isRefundState ? 'Ship-to' : 'Deliver To')}
          {isRefundState && renderAddressItem(storeAddressInfo, 'STORE ADDRESS')}
          {renderDeliveryMethod()}
        </>
      ) : (
        <>
          {isStoreAddressNeed && renderAddressItem(storeAddressInfo, 'Take')}
          {isBopsOrder && renderAddressItem({ ...bopsAddress, firstName, lastName }, 'Curbside Pickup')}
          {!isBopsOrder && !isStoreAddressNeed && (
            <>
              {renderAddressItem(shippingAddress, 'Deliver To')}
              {renderDeliveryMethod()}
            </>
          )}
        </>
      )}
    </Container>
  );
};

GeneralOrderInfo.propTypes = {
  classes: PropTypes.objectOf(PropTypes.string),
  email: PropTypes.string,
  orderId: PropTypes.string,
  transactionType: PropTypes.string,
  shipmentMethodName: PropTypes.string,
  shippingAddress: PropTypes.shape({}),
  storeAddressInfo: PropTypes.shape({}),
  bopsAddress: PropTypes.shape({}),
  isBopsOrder: PropTypes.bool,
  firstName: PropTypes.string,
  lastName: PropTypes.string,
  isVoid: PropTypes.bool,
  isReturn: PropTypes.bool,
};

GeneralOrderInfo.defaultProps = {
  classes: {},
  bopsAddress: {},
  email: '',
  firstName: '',
  lastName: '',
  shipmentMethodName: '',
  isBopsOrder: false,
  isVoid: false,
  isReturn: false,
};

export default GeneralOrderInfo;
